import React, { useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import { IResponse } from "constants/interfaces"

import useApi from "util/api"

// A tenant Tag as returned by GET /admin/tags. We only need slug + name for the picker.
interface AdminTag {
  id: string
  name: string
  slug: string
  category?: string | null
}

interface Props {
  // Currently-selected tag slugs (the bonus's allowed_segments).
  value: string[]
  onChange: (slugs: string[]) => void
  disabled?: boolean
}

/**
 * Iteration-3 Fix 11 — searchable multi-select of the tenant's real player tags/segments,
 * replacing the type-the-slugs text box. Fetches from GET /admin/tags (the same tag catalog
 * the admin manages), shows tag NAME, stores tag SLUG in allowed_segments. Selecting nothing
 * = no restriction (all players eligible), matching the backend "empty = all" semantic.
 *
 * A slug already stored on the bonus but no longer present in the catalog (renamed/deleted
 * tag) is still shown as a selected chip so the admin can see + remove it — it is not silently
 * dropped.
 */
export const TagMultiSelect: React.FC<Props> = ({ value, onChange, disabled }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [tags, setTags] = useState<AdminTag[]>([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const containerRef = useRef<HTMLDivElement>(null)

  // Load the tenant's tags once (a reasonably large page — tag catalogs are small).
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const { data } = await useApi.get<IResponse<{ payload: { data?: AdminTag[]; total?: number } }>>("/admin/tags", {
          params: { page: 1, pageSize: 200, sortRow: "name", sortOrder: "asc" }
        })
        if (!cancelled && data?.success) {
          setTags(data.response?.payload?.data ?? [])
        }
      } catch (error) {
        console.error("Failed to load tags:", error)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  // Close the dropdown on an outside click.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", onDocClick)
    return () => document.removeEventListener("mousedown", onDocClick)
  }, [])

  // Map slug → display name for chips (fall back to the slug for unknown/removed tags).
  const nameBySlug = useMemo(() => {
    const m = new Map<string, string>()
    tags.forEach((t) => m.set(t.slug, t.name))
    return m
  }, [tags])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return tags
    return tags.filter((t) => t.name.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q))
  }, [tags, search])

  const toggle = (slug: string) => {
    if (value.includes(slug)) {
      onChange(value.filter((s) => s !== slug))
    } else {
      onChange([...value, slug])
    }
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Selected chips */}
      {value.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {value.map((slug) => (
            <span
              key={slug}
              className="inline-flex items-center gap-1 rounded-full bg-[rgba(132,0,168,0.15)] px-2 py-1 text-xs dark:text-white"
            >
              {nameBySlug.get(slug) ?? slug}
              <button
                type="button"
                disabled={disabled}
                className="ml-1 cursor-pointer opacity-70 hover:opacity-100"
                onClick={() => toggle(slug)}
                aria-label={`Remove ${nameBySlug.get(slug) ?? slug}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Trigger / search box */}
      <input
        type="text"
        disabled={disabled}
        placeholder={(loading ? t("bonus2.shared.loadingTags") : t("bonus2.shared.searchTags")) as string}
        value={search}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setSearch(e.target.value)
          setOpen(true)
        }}
        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
      />

      {open && (
        <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800">
          {loading && <div className="px-3 py-2 text-sm text-gray-500">{t("bonus2.shared.loading")}</div>}
          {!loading && filtered.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-500">{(tags.length === 0 ? t("bonus2.shared.noTagsForTenant") : t("bonus2.shared.noMatches")) as string}</div>
          )}
          {!loading &&
            filtered.map((t) => {
              const checked = value.includes(t.slug)
              return (
                <label
                  key={t.id}
                  className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700"
                >
                  <input
                    type="checkbox"
                    className="checkbox checkbox-primary checkbox-sm"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => toggle(t.slug)}
                  />
                  <span>{t.name}</span>
                  <span className="ml-auto text-xs opacity-50">{t.slug}</span>
                </label>
              )
            })}
        </div>
      )}
    </div>
  )
}
